import * as React from 'react'
import styled from 'styled-components'
import { ResponsiveCirclePacking } from '@nivo/circle-packing'
import { CollectorCircle } from '../leaderboard/CollectorCircle'
import { CollectorTooltip } from '../leaderboard/CollectorTooltip'
import { useMediaQuery } from '@material-ui/core'
import { DefaultCircle } from '../leaderboard/DefaultCircle'
import EmptyState from '../leaderboard/EmptyState'
import { VideoData } from '../../../interfaces'
import { COLLECTORS_SUBSCRIPTION, SECONDARY_SUBSCRIPTION } from '../../../graphql/graphql-queries'
import { useSubscription } from '@apollo/client'
import { Query_Root, TokenAccount } from '../../../graphql/indexer-graphql-types'
import { CIRCLE_MOCK_DATA } from '../../../data/Circle'
import { FbEnrichedUser } from '../../../interfaces/User'
import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'

interface EnrichedCollector {
  name: string
  value: number
  owned: number
  time: Date
  user?: FbEnrichedUser
}

type CollectorsDetailsProps = {
  video: VideoData
}

const CollectorsDetails = ({ video }: CollectorsDetailsProps): JSX.Element => {
  const isMobile = useMediaQuery('(max-width: 43em)')
  const collectors = useSubscription<Query_Root>(COLLECTORS_SUBSCRIPTION, {
    variables: {
      mint: video.address,
    },
  }).data
  const secondaryData = useSubscription<Query_Root>(SECONDARY_SUBSCRIPTION, {
    variables: {
      mint: video.address,
    },
  }).data
  const secondaryMint = secondaryData?.mint[0]
  const [enrichedCollectors, setEnrichedCollectors] = React.useState<EnrichedCollector[]>([])

  const enrichCollectors = async (): Promise<void> => {
    const collectorsTokenAccounts: TokenAccount[] = collectors?.mint[0]?.tokenAccounts || []

    const ids = collectorsTokenAccounts.map(({ owner }) => owner)
    const chunks = []
    const chunkSize = 10
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize)
      chunks.push(chunk)
    }

    const chunkedUsers = await Promise.all(
      chunks.map(async (ids) => {
        return await fbEnrichUsersFromIds(ids)
      })
    )

    const users = chunkedUsers.reduce((acc, val) => acc.concat(val), [])

    const enriched = collectorsTokenAccounts.map((item) => {
      return {
        name: item.owner,
        value: item.amount,
        owned: item.amount,
        time: new Date(item.createdAt),
        user: users.find((user) => user.address === item.owner),
      }
    })
    setEnrichedCollectors(enriched)
  }

  React.useEffect(() => {
    enrichCollectors()
  }, [collectors])

  return (
    <OuterDiv>
      <TextDiv>
        <TitleDiv>
          <Title>The Circle</Title>
          {enrichedCollectors.length > 0 && (
            <DetailsDiv>
              {/* <DetailsTitle>SUPPLY</DetailsTitle>
              <DetailsContent>{secondaryMint?.editionMetadata?.maxSupply ?? 0}</DetailsContent> */}
              <DetailsTitle>OWNERS</DetailsTitle>
              <DetailsContent end>
                {secondaryMint?.tokenAccountsAggregate.aggregate?.count ?? 0}
              </DetailsContent>
            </DetailsDiv>
          )}
        </TitleDiv>
        <Description>
          {`By purchasing an NFT for this video, collectors earn a spot in The Circle.${
            !isMobile ? `\n` : ' '
          }Sized by # of NFTs owned.`}
        </Description>
      </TextDiv>
      <InnerDiv>
        <CircleDiv>
          {enrichedCollectors.length > 0 ? (
            <ResponsiveCirclePacking
              data={{
                name: '',
                children: enrichedCollectors,
              }}
              id="name"
              colors={{ scheme: 'greys' }}
              colorBy="id"
              tooltip={CollectorTooltip}
              circleComponent={CollectorCircle}
              padding={20}
              leavesOnly={false}
              animate={true}
            />
          ) : (
            <ResponsiveCirclePacking
              data={CIRCLE_MOCK_DATA}
              id="name"
              colors={{ scheme: 'greys' }}
              colorBy="id"
              tooltip={() => <></>}
              circleComponent={(props) => {
                if (props.node.id === '') {
                  return CollectorCircle(props)
                } else {
                  return DefaultCircle(props)
                }
              }}
              padding={20}
              leavesOnly={false}
              animate={true}
            />
          )}
        </CircleDiv>
        {!enrichedCollectors.length && <EmptyState video={video} />}
      </InnerDiv>
    </OuterDiv>
  )
}

export default CollectorsDetails

const OuterDiv = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  align-items: center;
  margin-left: 12px;
  margin-right: 12px;
  background: linear-gradient(rgb(30, 30, 30), rgb(18, 18, 18));
  border-radius: 15px;
  padding: 15px;
  margin-top: 15px;
  border-color: #323232;
  border-width: 1px;
  border-style: solid;
  width: calc(100% - 56px);

  @media (min-width: 43em) {
    margin: 0;
    margin-top: 25px;
    padding: 25px;
    width: calc(100% - 52px);
  }
`

const InnerDiv = styled.div`
  margin-top: 25px;
  position: relative;
  width: 100%;
  padding-top: 100%;

  @media (min-width: 43em) {
    width: 70%;
    padding-top: 70%;
  }

  @media (min-width: 62em) {
    width: 60%;
    padding-top: 60%;
  }

  @media (min-width: 82em) {
    width: 55%;
    padding-top: 55%;
  }
`

const CircleDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`

const TitleDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const DetailsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const DetailsTitle = styled.div`
  color: #cdcdcd;
  font-weight: 400;

  // mobile
  font-size: 12px;

  // tablet
  @media (min-width: 43em) {
    font-size: 14px;
  }

  // small desktop+
  @media (min-width: 62em) {
    font-size: 14px;
  }
`

const DetailsContent = styled.div<{ end?: boolean }>`
  color: white;
  font-weight: bold;

  // mobile
  font-size: 18px;
  margin-left: 10px;
  margin-right: ${(props) => (!props.end ? '30px' : '0')};

  // tablet
  @media (min-width: 43em) {
    font-size: 24px;
    margin-left: 7.5px;
    margin-right: ${(props) => (!props.end ? '20px' : '0')};
  }

  // small desktop+
  @media (min-width: 62em) {
    font-size: 26px;
    margin-left: 10px;
    margin-right: ${(props) => (!props.end ? '30px' : '0')};
  }
`

const Title = styled.div`
  color: white;
  font-size: 22px;
  font-weight: 700;
  text-align: left;

  @media (min-width: 43em) {
    font-size: 40px;
  }
`

const Description = styled.div`
  color: #cdcdcd;
  font-size: 14px;
  font-weight: 400;
  white-space: pre-wrap;
  text-align: left;
  margin-top: 15px;
  /* width: 90%; */

  @media (min-width: 43em) {
    font-size: 18px;
  }
`
